<template>
  <Modal
    v-model:visible="isVisible"
    :header="t('common.addParam', { param: stopLabel })"
    class="add-stop-to-shipment-modal"
    @keypress.enter="onConfirm"
    @keypress.esc="onCancel"
  >
    <p id="add-stop-instructions">
      {{
        t('routeBuilder.addStopInstructions', {
          stopLabel: lowerCase(stopLabel),
        })
      }}
    </p>

    <form id="add-stop" aria-labelledby="add-stop-instructions">
      <AddStopEdit
        id="new-stop"
        v-model:location="location"
        :stop-type="stopType"
        :stop-selector="stopSelector"
        append-to="body"
        autofocus
        @update:stop-type="internalStopType = $event"
      />
    </form>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        mode="primary"
        :icon="PrimeIcons.PLUS"
        :label="t('common.addParam', { param: stopLabel })"
        @click="onConfirm"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useValidations } from '@rygen/rygen-ui/composables';
import { lowerCase } from '@rygen/rygen-ui/i18n';
import { Location, Optional } from '@rygen/rygen-ui/model';

import { EmbeddableLocation, ShipmentStopType } from '@/model';
import {
  AddStopPayload,
  AddStopToShipmentPayload,
  SHIPMENT_STOP_TYPES_BY_STOP_SELECTOR,
  StopSelector,
  TRANSLATIONS_BY_STOP_SELECTOR,
} from '@/model/routeBuilder';

import AddStopEdit from '@/components/routeBuilder/addStopToShipment/AddStopEdit.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  stopType: {
    type: String as PropType<ShipmentStopType>,
    default: () => undefined,
  },
  stopSelector: {
    type: String as PropType<StopSelector>,
    default: () => 'intermediate',
  },
});
const { stopType, stopSelector } = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'confirm', payload: AddStopPayload): void;
}>();

const { t } = useI18n();

const location: Ref<Optional<Location>> = ref();
const internalStopType: Ref<Optional<AddStopToShipmentPayload['stopType']>> =
  ref(stopType?.value);

const stopLabel = computed(() => {
  if (stopSelector.value === 'intermediate' && stopType?.value) {
    return stopType.value === ShipmentStopType.PICKUP
      ? t('shipments.pickups')
      : t('shipments.dropoffs');
  } else {
    return TRANSLATIONS_BY_STOP_SELECTOR[stopSelector.value];
  }
});

const internalValue = computed(() => ({
  location: location.value
    ? EmbeddableLocation.fromLocation(location.value)
    : undefined,
  stopType: internalStopType.value,
}));
const { v$, validate } = useValidations(internalValue, {}, 'add-stop-modal');

const onConfirm = async () => {
  if (!(await validate())) {
    return;
  }

  isVisible.value = false;
  // validation ensures these are defined
  emit('confirm', {
    location: internalValue.value.location!,
    stopType: internalValue.value.stopType!,
    stopSelector: stopSelector.value,
  });
};

const onCancel = () => {
  isVisible.value = false;
  emit('cancel');
};

watch(isVisible, () => {
  location.value = undefined;
  internalStopType.value =
    stopType?.value ?? SHIPMENT_STOP_TYPES_BY_STOP_SELECTOR[stopSelector.value];
  v$.value.$reset();
});
</script>

<style>
.add-stop-to-shipment-modal {
  #add-stop-instructions {
    margin-bottom: var(--sp-3);
  }

  #new-stop {
    display: flex;
    flex-direction: column;
    gap: var(--gap-1);
  }
}
</style>
